// Route chunk loading that survives a deploy.
//
// Every page in Game Lite is `lazy()`-loaded, which means the running tab holds
// URLs for content-hashed chunks that only exist for as long as that build is
// on the CDN. Ship a deploy while a room is mid-tasting and the NEXT navigation
// asks for a chunk that is no longer there: `import()` rejects, and with nothing
// catching it the player gets a white screen for the rest of the night.
//
// Two layers deal with it:
//
//   1. Retry once after a short delay. Most failures are a blip on venue wifi,
//      not a deploy, and a second attempt just works.
//   2. If it still fails, reload the page ONCE. A reload fetches a fresh
//      index.html and therefore fresh chunk URLs, which is the only real cure
//      for a stale build. The one-shot flag lives in sessionStorage so a
//      genuinely broken build can't put the tab into a reload loop — the second
//      failure falls through to the error boundary instead.
//
// Nothing is lost by reloading: the session and the durable write queue are both
// in localStorage, so a player comes back to the same place with their unsent
// answers intact.

import { lazy } from 'react';

const RELOAD_FLAG = 'tasted:solo:chunkReload';

/** True for the various ways a browser reports "that chunk isn't there". */
export function isChunkLoadError(err) {
  const message = String(err?.message || err || '');
  return (
    err?.name === 'ChunkLoadError' ||
    /Loading chunk|Failed to fetch dynamically imported module|error loading dynamically imported module|Importing a module script failed/i.test(
      message
    )
  );
}

function hasReloadedAlready() {
  try {
    return sessionStorage.getItem(RELOAD_FLAG) === '1';
  } catch {
    // Private mode / storage blocked — treat as "already reloaded" so we never
    // risk a loop we have no way of remembering we started.
    return true;
  }
}

function markReloaded() {
  try {
    sessionStorage.setItem(RELOAD_FLAG, '1');
  } catch {
    /* ignore — the check above already fails closed */
  }
}

/** Clear the one-shot flag once the app is demonstrably healthy. */
export function clearChunkReloadFlag() {
  try {
    sessionStorage.removeItem(RELOAD_FLAG);
  } catch {
    /* ignore */
  }
}

const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * `React.lazy` with the recovery above.
 *
 * @param {() => Promise<{default: React.ComponentType}>} factory
 */
export function lazyWithRetry(factory) {
  return lazy(async () => {
    try {
      return await factory();
    } catch {
      // A blip deserves a second go before we consider anything drastic.
      await delay(400);
      try {
        return await factory();
      } catch (second) {
        if (isChunkLoadError(second) && !hasReloadedAlready()) {
          markReloaded();
          window.location.reload();
          // Hang this promise: the reload is already under way and resolving
          // (or rejecting) here would only flash an error the player can't act on.
          return await new Promise(() => {});
        }
        throw second;
      }
    }
  });
}

export { RELOAD_FLAG };
